// Repository layer responsible for direct communication
// with APIs or other data sources (HTTP, GraphQL, etc.)
import featureFlagsRepository from '../repositories/featureFlagsRepository';

/**
 * featureFlagsService
 * --------------------
 * Service layer acting as an abstraction between:
 * - UI components (FeatureFlags, FeatureFlagItem, etc.)
 * - Repository layer (actual API calls)
 *
 * Why this layer exists:
 * - Central place for business logic
 * - Allows response/request transformations
 * - Enables caching, validation, or analytics later
 * - Keeps UI components thin and API-agnostic
 */
const featureFlagsService = {
  /**
   * Fetches all feature flags for a given SyncUp application.
   *
   * Flow:
   * UI --> Service --> Repository --> API
   *
   * @param {Object} params
   * @param {string} params.syncUpAppName
   *   Name of the SyncUp application whose feature flags are requested
   *
   * @returns {Promise<Array>}
   *   Resolves with an array of feature flag objects
   */
  getFeatureFlags: async ({ syncUpAppName }) => {
    return await featureFlagsRepository.getFeatureFlags({ syncUpAppName });
  },

  /**
   * Creates a new feature flag for a SyncUp application.
   *
   * @param {Object} params
   * @param {string} params.syncUpAppName
   *   Name of the SyncUp application
   * @param {Object} params.body
   *   Payload containing feature flag details
   *   (e.g., featureId, isEnabled, description)
   *
   * @returns {Promise<Object>}
   *   API response for the created feature flag
   */
  createFeatureFlag: async ({ syncUpAppName, body }) => {
    return await featureFlagsRepository.createFeatureFlag({
      syncUpAppName,
      body
    });
  },

  /**
   * Deletes an existing feature flag.
   *
   * @param {Object} params
   * @param {string} params.featureId
   *   Unique identifier of the feature flag to delete
   * @param {string} params.syncUpAppName
   *   Name of the SyncUp application
   *
   * @returns {Promise<void>}
   *   Resolves when deletion is successful
   */
  deleteFeatureFlag: async ({ featureId, syncUpAppName }) => {
    return await featureFlagsRepository.deleteFeatureFlag({
      featureId,
      syncUpAppName
    });
  }

  /**
   * Update feature flag (future extension point)
   * ------------------------------------------------
   * This is intentionally commented out.
   * When enabled, business rules such as:
   * - validation
   * - audit logging
   * - partial updates
   * can be implemented here without touching UI components.
   */
  // updateFeatureFlag: async ({ featureId, syncUpAppName, body }) => {
  //   return await featureFlagsRepository.updateFeatureFlag({
  //     featureId,
  //     syncUpAppName,
  //     body
  //   });
  // }
};

export default featureFlagsService;
